"use client";

// Graceful sector-level guidance for lift-served bike parks whose gravity
// trails are not yet in OpenStreetMap (so the routing graph is thin from the
// lift top). Instead of a broken or empty itinerary, the planner is honest:
// this is a real bike park, here is what it offers, ride from the top, and
// here is the resort's official trail map. Facts only, sourced and attributed;
// no fabricated routing. See lib/planner/bikepark-knowledge.ts.

import type { BikeParkInfo } from "@/lib/planner/bikepark-knowledge";
import { bikeParkAttribution } from "@/lib/planner/bikepark-knowledge";

const GRADE_DOT: Record<string, string> = {
  green: "#3F8E3F",
  blue: "#2E6FB8",
  red: "#C2443B",
  black: "#222",
  "double-black": "#222",
};

function gradeDot(grade: string): string {
  const key = grade.toLowerCase().replace(/\s+/g, "-");
  return GRADE_DOT[key] ?? "#7A7A7A";
}

type Props = {
  parks: BikeParkInfo[];
  /** Emphasise the card when the generated plan came back thin/empty. */
  planThin?: boolean;
};

export function BikeParkGuidance({ parks, planThin = false }: Props) {
  if (parks.length === 0) return null;

  return (
    <section
      className={`rounded-[16px] border p-4 md:p-5 ${
        planThin ? "border-gold/40 bg-gold/[0.06]" : "border-border-light bg-warm-bg"
      }`}
    >
      <h3 className="font-serif text-[16px] font-bold text-navy">
        {parks.length === 1 ? "Bike park here" : "Bike parks here"}
      </h3>
      <p className="mt-1 text-[12.5px] leading-snug text-text-muted">
        {planThin
          ? "The available mapping, access and your preferences did not produce a full ride. Explore the resort’s own trail map for more options."
          : "Explore these lift-served parks and their official trail maps. Some descents or connections may still be missing from the planner."}
      </p>

      <ul className="mt-3 space-y-3">
        {parks.map((park) => (
          <li key={park.slug} className="rounded-[12px] border border-border-light bg-white p-3.5">
            <div className="flex items-baseline justify-between gap-2">
              <h4 className="text-[14.5px] font-semibold text-navy">{park.name}</h4>
              {park.runCount != null ? (
                <span className="shrink-0 text-[12px] text-text-muted">
                  {park.runCount} runs
                </span>
              ) : null}
            </div>
            <p className="mt-0.5 text-[12.5px] text-text-muted">
              {park.gradeRange}
              {park.servingLifts.length > 0
                ? ` · from ${park.servingLifts.slice(0, 3).join(", ")}`
                : ""}
            </p>

            {park.marqueeRuns.length > 0 ? (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {park.marqueeRuns.slice(0, 6).map((run) => (
                  <li
                    key={run.name}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border-light bg-warm-bg px-2.5 py-1 text-[11.5px] text-navy"
                  >
                    <span
                      aria-hidden
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: gradeDot(run.grade) }}
                    />
                    {run.name}
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="mt-3 flex items-center justify-between gap-2">
              <a
                href={park.trailMapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[44px] items-center rounded-sm bg-navy px-3.5 text-[12.5px] font-semibold text-white no-underline transition-colors hover:bg-navy/90"
              >
                Official trail map
              </a>
              <span className="text-[10.5px] text-text-muted">{bikeParkAttribution(park)}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
